import type { RouteMeta, RouteRecordRaw } from 'vue-router'
import { defineAsyncComponent, ref, type Ref } from '@/composition/vue/compositionApi'
import { getGlobalStateService } from '@/core/container/ioc'

export type SidebarStateKey = 'sidebarWidth' | 'settingsSidebarWidth'

const defaultSidebarCollapsed = false

const sidebarWidths: Partial<Record<SidebarStateKey, Ref<number>>> = {}
const sidebarCollapsed = ref()
const sidebarShowTransition = ref(true)

const _defaultSidebarWidth = 260

export const useSidebarState = (defaultSidebarWidth?: number, initSidebarStateKey?: SidebarStateKey) => {
  const globalStateService = getGlobalStateService()

  const sidebarStateKey: SidebarStateKey = initSidebarStateKey ?? 'sidebarWidth'
  const getInitialSidebarWidth = () =>
    (globalStateService.getFieldValueSync(sidebarStateKey) as number | undefined) ??
    defaultSidebarWidth ??
    _defaultSidebarWidth

  let sidebarWidth = sidebarWidths[sidebarStateKey]
  if (!sidebarWidth) {
    sidebarWidth = ref(getInitialSidebarWidth())
    sidebarWidths[sidebarStateKey] = sidebarWidth
  } else if (defaultSidebarWidth !== undefined) {
    sidebarWidth.value = getInitialSidebarWidth()
  }
  sidebarCollapsed.value = globalStateService.getFieldValueSync('sidebarCollapsed') ?? defaultSidebarCollapsed

  const hasSavedSidebarWidthValue = () => {
    return !!globalStateService.getFieldValueSync(sidebarStateKey)
  }

  const setSidebarWidth = (value: number, saveToStore = true) => {
    sidebarWidth.value = value
    if (saveToStore) {
      globalStateService.setFieldValueIfChanged(sidebarStateKey, value)
    }
  }

  const setSidebarCollapsed = (value: boolean) => {
    sidebarCollapsed.value = value

    globalStateService.setFieldValueIfChanged('sidebarCollapsed', value)
  }

  const setSidebarShowTransition = (value: boolean) => {
    sidebarShowTransition.value = value
  }

  return {
    sidebarWidth,
    hasSavedSidebarWidthValue,
    setSidebarWidth,
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarShowTransition,
    setSidebarShowTransition,
  }
}

export const useSidebarMenuComponent = (route: { meta?: RouteMeta }): RouteRecordRaw['component'] | null => {
  if (route.meta?.menu) {
    if (typeof route.meta.menu === 'function') {
      return defineAsyncComponent(route.meta?.menu)
    }

    return route.meta?.menu
  }

  return null
}
